import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';

import { type DecisionRefusalProps } from './DecisionRefusal.types';

/**
 * A refused decision, drawn where the moderator is looking.
 *
 * **Inside the dialog and never a toast.** A colleague's verdict is news this
 * moderator has to act on, and a toast takes it away before it has been read
 * (P19b). The dialog also stays open: closing it would answer a refusal by
 * hiding the thing the refusal is about.
 *
 * **One refusal, two recoveries.** A 409 means somebody decided first and is
 * **right**, so the offer is a re-read — a retry would be refused again. A 404
 * means there is nothing to go and read, so the offer is to close. Collapsing
 * them would give the operator a refresh button pointing at nothing.
 *
 * Shared by all three decision dialogs, so the same refusal cannot get three
 * slightly different answers.
 */
export function DecisionRefusal({
  message,
  isConflict,
  isGone,
  onReload,
  onClose,
}: DecisionRefusalProps) {
  const { t } = useTranslation('violations');

  if (message === null) {
    return null;
  }

  return (
    <div role="alert" className="flex flex-col gap-sm rounded-md border border-destructive p-md">
      <p className="flex items-start gap-sm text-body text-destructive">
        <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{message}</span>
      </p>

      {isConflict || isGone ? (
        <div>
          <Button
            variant="secondary"
            onClick={() => {
              if (isGone) {
                onClose();
              } else {
                onReload();
              }
            }}
          >
            {t(isGone ? 'review.close' : 'review.refresh')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
